import type { AddressViewModel, AddressWithoutGeolocationViewModel } from '@generated';
import { useMutation } from '@tanstack/react-query';
import { useAppSettings } from '~/AppSettingsContext';
import type { AzureMapsSearchResult } from './useAzureMapsAutocomplete';

interface AzureMapsResponse {
	results: AzureMapsSearchResult[];
}

function buildSearchQuery(address: Partial<AddressViewModel>): string {
	return [address.addressOne, address.city, address.state, address.zipCode].filter(Boolean).join(', ');
}

export function useAzureMapsGeocode() {
	const { atlasSubscriptionKey } = useAppSettings();

	return useMutation({
		mutationFn: async (address: AddressWithoutGeolocationViewModel): Promise<AddressViewModel> => {
			const query = buildSearchQuery(address);
			const url = new URL('https://atlas.microsoft.com/search/address/json');
			url.searchParams.set('api-version', '1.0');
			url.searchParams.set('subscription-key', atlasSubscriptionKey);
			url.searchParams.set('query', query);
			url.searchParams.set('limit', '1');

			const response = await fetch(url.toString());
			if (!response.ok) throw new Error('Geocoding failed');

			const data: AzureMapsResponse = await response.json();
			const result = data.results?.[0];

			if (!result) throw new Error('No geocoding results found');

			return {
				...address,
				latitude: result.position.lat,
				longitude: result.position.lon,
				mapIntegrationId: result.id,
				formattedAddress: address.formattedAddress || result.address.freeformAddress
			};
		}
	});
}
